import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IToggleableKeyInputs } from "../../../domain";
// NOTE: this slice deliberately imports nothing from `pages/File/Editor/classes/EditorFile`.
// It used to, in order to look up read-only state from inside reducers, which
// made the reducers impure and created a circular import between the store and
// the editor. Everything a reducer needs now arrives in the action payload.

interface EditorState {
    enabled: boolean;
    taar:boolean;
    mandra:boolean;
    kanSwar:boolean;
    keyboardSettings:Record<IToggleableKeyInputs,boolean>;
    /** Whether the editor is currently in preview (non-editing) mode. */
    readOnlyMode:boolean;
    /**
     * Whether this viewer is allowed to edit at all (i.e. owns the file).
     * Captured when the file is opened so reducers never need to look it up.
     */
    canEdit:boolean;
    fileId:string|null;
    showHelp:boolean;
}

const initialState: EditorState = {
    enabled:true,
    taar:false,
    mandra:false,
    kanSwar:false,
    keyboardSettings:{
        "s":true,
        "r":false,
        "R":true,
        "g":false,
        "G":true,
        "m":true,
        "M":false,
        "p":true,
        "d":false,
        "D":true,
        "n":false,
        "N":true,
    },
    readOnlyMode:true,
    canEdit:false,
    fileId:null,
    showHelp:false
};

const editorSlice = createSlice({
    name: "editor",
    initialState,
    reducers: {
        toggleEnabled: (state, action: PayloadAction<boolean>) => {
            state.enabled=action.payload
            if(action.payload){
                state.kanSwar=false
                state.mandra=false
                state.taar=false
            }
        },
        toggleTaar: (state, ) => {
            state.taar=!state.taar
            if(state.taar){
                state.mandra=false
            }
        },
        toggleMandra: (state,) => {
            state.mandra=!state.mandra
            if(state.mandra){
                state.taar=false
            }
        },
        toggleKanSwar: (state, ) => {
            state.kanSwar=!state.kanSwar
        },
        toggleKeyboardSettings:(state, action:PayloadAction<IToggleableKeyInputs>)=>{
            state.keyboardSettings[action.payload]=!state.keyboardSettings[action.payload]
        },
        // Preview toggle. Gated on `canEdit`, which is captured when the file
        // is opened — NOT looked up here.
        toggleReadOnlyMode:(state)=>{
            if(state.canEdit){
                state.readOnlyMode=!state.readOnlyMode
            }
        },
        /**
         * Called when a file is opened. `readOnly` is supplied by the caller.
         *
         * IMPORTANT: this reducer must stay pure. It previously called
         * `EditorFile.getInstance(...)` to derive read-only, which reached
         * outside Redux from inside a reducer. Once EditorFile started reading
         * its state from the store, that became a hard runtime error
         * ("You may not call store.getState() while the reducer is executing").
         * Ownership is a property of (viewer, document) and belongs in the
         * action, not in a lookup.
         */
        setFileIdForEditor:(
            state,
            action:PayloadAction<{fileId:string; readOnly:boolean}>
        )=>{
            state.fileId=action.payload.fileId;
            state.enabled=true;
            state.canEdit=!action.payload.readOnly;
            state.readOnlyMode=action.payload.readOnly;
        },
        toggleShowHelp:(state,action:PayloadAction<boolean>)=>{
            state.showHelp=action.payload
        }
    },
});

export const { toggleEnabled,toggleKanSwar,toggleMandra,toggleTaar,toggleKeyboardSettings,toggleReadOnlyMode,setFileIdForEditor, toggleShowHelp} =
    editorSlice.actions;

export default editorSlice.reducer;
